import React, { memo, useMemo, useCallback } from 'react';
import { Stack, Typography } from '@mui/material';
import { SwellArrowIcon } from '@wavetrak/theme';
import { round, isFinite } from 'lodash';
import { useMinWidthTabletLarge } from 'hooks/useMediaQueries';
import { BuoySpectraFormattedData } from 'types/buoys';

import styles from './ForecastGraphSwellSpectraReadings.module.scss';

interface Props {
  buoySpectraReadingData: BuoySpectraFormattedData | null | undefined;
  direction: Array<number>;
  energy: Array<number>;
  isBuoySwellSpectraGraph?: boolean;
  period: Array<number>;
  spread: Array<number>;
  timestamp: number;
}

const ForecastGraphSwellSpectraReadings: React.FC<Props> = ({
  buoySpectraReadingData,
  direction,
  energy,
  isBuoySwellSpectraGraph,
  period,
  spread,
  timestamp,
}) => {
  const currentDirection = useCallback((index: number) => round(direction[index]), [direction]);
  const currentSpread = useCallback((index: number) => round(spread[index]), [spread]);
  const currentEnergy = useCallback((index: number) => round(energy[index], 2), [energy]);
  const isDesktop = useMinWidthTabletLarge();

  const buoyPageReadingData = useMemo(
    () =>
      isDesktop
        ? buoySpectraReadingData
        : buoySpectraReadingData?.flatMap((value, index) =>
            index === buoySpectraReadingData.length - 1 ? [value] : [value, null],
          ),
    [buoySpectraReadingData, isDesktop],
  );

  return (
    <div className={styles.swellSpectraReadingsWrapper}>
      <div className={styles.periodDivider} />
      <div className={styles.directionDivider} />

      <Stack direction="row" className={styles.dataRow}>
        <div className={styles.labelsWrapper}>
          <Typography variant="caption2" className={styles.periodLabel} data-testid="period-label">
            Period
          </Typography>
          <br />
          <Typography
            variant="caption2"
            className={styles.directionLabel}
            data-testid="direction-label"
          >
            Direction
          </Typography>
        </div>
        {isBuoySwellSpectraGraph
          ? buoyPageReadingData?.map((periodData: any, index: number) => (
              <div
                key={`${index + 1}-${timestamp}-${periodData?.period}`}
                className={styles.readingWrapper}
                data-testid="swell-spectra-reading-wrapper"
              >
                {periodData?.period && (
                  <>
                    <Typography variant="callout2" className={styles.period}>
                      {periodData?.period}s
                    </Typography>
                    {periodData?.energy > 0.01 && (
                      <div className={styles.reading} data-testid="swell-spectra-reading">
                        {isFinite(periodData?.direction) && (
                          <div className={styles.swellArrow}>
                            <SwellArrowIcon size="large" rotation={periodData.direction - 180} />
                          </div>
                        )}
                        <div>
                          <Typography variant="caption2" className={styles.direction}>
                            {periodData?.direction}&deg;
                          </Typography>
                        </div>
                        <div>
                          <Typography variant="callout2" className={styles.spread}>
                            {periodData?.spread}&deg;
                          </Typography>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))
          : period?.map((periodData: any, index: number) => (
              <div
                key={`${periodData}-${timestamp}-${index + 1}`}
                className={styles.readingWrapper}
                data-testid="swell-spectra-reading-wrapper"
              >
                {periodData && (
                  <>
                    <Typography variant="callout2" className={styles.period}>
                      {periodData}s
                    </Typography>
                    {currentEnergy(index) > 0.01 && (
                      <div className={styles.reading} data-testid="swell-spectra-reading">
                        <div className={styles.swellArrow}>
                          <SwellArrowIcon size="large" rotation={currentDirection(index) - 180} />
                        </div>
                        <div>
                          <Typography variant="caption2" className={styles.direction}>
                            {currentDirection(index)}&deg;
                          </Typography>
                        </div>
                        <div>
                          <Typography variant="callout2" className={styles.spread}>
                            {currentSpread(index)}&deg;
                          </Typography>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
      </Stack>
    </div>
  );
};

export default memo(ForecastGraphSwellSpectraReadings);
